import type { AssetType } from '@eis/core';
import { SYSTEM_POSITION_COLUMNS } from './deckung';

/**
 * The asset document while it is being edited, and the rules for changing it.
 *
 * It is a plain `Record<string, unknown>` and stays one on purpose. The document's shape IS the
 * JSON Schema (backend standard §20); giving it a hand-written TypeScript type here would be a
 * second, weaker copy of the contract that stops matching the day the schema gains a field —
 * and the failure mode of that copy is the worst available one: a field silently dropped on save.
 *
 * So the editor reads and writes through pointers, and everything it does not have a control for
 * is carried through untouched.
 */

export type AssetDocument = Record<string, unknown>;

export const isRecord = (value: unknown): value is AssetDocument =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export const asRecordArray = (value: unknown): AssetDocument[] => asArray(value).filter(isRecord);

/**
 * Replaces one property of one array element, leaving every other key on that element alone.
 *
 * "Leaving the rest alone" is the whole contract of this function. `field` carries `isUnique`,
 * `order` and `expression` that this console has no control for; a row editor that rebuilt each
 * element from the controls it drew would delete them on the first keystroke.
 */
export function setInRow(
  rows: readonly AssetDocument[],
  index: number,
  key: string,
  value: unknown,
): AssetDocument[] {
  return rows.map((row, at) => {
    if (at !== index) return row;
    // `undefined` means "remove the key", which is not the same as a null or an empty string: an
    // optional numeric bound that has been cleared must be ABSENT, or the schema sees a null
    // where it expects a number.
    if (value === undefined) {
      const { [key]: _removed, ...rest } = row;
      return rest;
    }
    return { ...row, [key]: value };
  });
}

export const removeRow = (rows: readonly AssetDocument[], index: number): AssetDocument[] =>
  rows.filter((_row, at) => at !== index);

export const appendRow = (
  rows: readonly AssetDocument[],
  row: AssetDocument,
): AssetDocument[] => [...rows, row];

/** Swaps two adjacent rows. Out-of-range moves are no-ops rather than errors. */
export function moveRow(
  rows: readonly AssetDocument[],
  from: number,
  to: number,
): AssetDocument[] {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
    return [...rows];
  }
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...rows];
  next.splice(to, 0, moved);
  return next;
}

/**
 * The empty document a new draft of each type starts from.
 *
 * Only the REQUIRED containers, and empty. It is not a template and must not become one: seeding
 * a new asset with example fields, a specimen lookup or a plausible premium expression would be
 * inventing configuration (CLAUDE.md rule 8b), and an operator who did not notice would publish
 * it. What the schema says must be present is present; everything else is theirs to add.
 *
 * `basedOnName` is the real answer to "I do not want to start from nothing" — the service seeds
 * the new draft from an existing asset, which is data somebody already signed off on.
 */
export function emptyDocumentFor(type: AssetType): AssetDocument {
  switch (type) {
    case 'insurance_core':
      // The three CLIENT sections (SDD-010 §5.7). `namedRanges` and `factors` are the service's:
      // they are derived from uploaded files, and a document that carried them is refused.
      return { insuredObjects: [], questionGroups: [], premiumItems: [] };
    case 'group_core':
      return {
        aggregation: { mode: 'SUM_CHILD_PREMIUMS' },
        groupFactors: [],
        billingDefaults: { mode: 'AGGREGATE_CHILD_PREMIUMS', frequency: 'MONTHLY' },
        // Present and empty, so the day SSOT I-7 is answered the asset does not change shape.
        conditionBundles: [],
      };
    case 'tarif_deckung':
      return {
        tariffVariations: [],
        categoryDefinitions: [],
        claimConfig: {
          generalSetting: {
            settlementType: 'PARTIAL_CLAIM',
            currency: 'EUR',
            roundingStrategy: 'NONE',
          },
          // v1's system columns in v1's order (ui-spec §3.2): the gate requires every default
          // column in `fieldsOrder` (POSITION_FIELDS_ORDER_INCOMPLETE), so an empty draft starts
          // with them — v1's defaults, not invented configuration.
          claimPositionFields: { customFields: [], fieldsOrder: [...SYSTEM_POSITION_COLUMNS] },
        },
      };
    case 'lead_lifecycle':
      // The three required containers, empty (SDD-013 §6.2). `initial` is a code the operator
      // declares first; `expiry` is optional and absent until they add it.
      return { initial: '', statuses: [], transitions: [] };
    case 'billing_config':
      // The TARIF profile's knobs at the platform default (SDD-015 §5.5): a tarif's asset is total,
      // and starting from the default is starting from what an unpinned version bills under. The
      // group editor (prompt 21 phase 6) starts from `{ profile: 'GROUP_TARIF' }` instead — an
      // override that sets nothing inherits everything.
      return {
        profile: 'TARIF',
        billingAnchor: 'POLICY_START',
        billingFrequencyUnit: 'MONTH',
        billingFrequencyValue: 1,
        firstPeriodAlignment: 'NONE',
        invoiceLeadDays: 0,
        paymentTerm: { kind: 'DAYS_AFTER_INVOICE', days: 14 },
        dayCountBasis: 'CALENDAR_DAYS',
        midTermChange: 'PRO_RATA_DAILY',
        terminationRefund: 'PRO_RATA',
        currency: 'EUR',
        roundingMode: 'HALF_UP',
        roundingScope: 'PER_LINE',
        tax: { code: 'A1', rate: '0.19', includedInPremium: true },
        minCorrectionAmount: 0,
        estimation: { enabled: true },
      };
  }
}

/**
 * `group_core` still carries a hand-written published schema, edited raw. `tarif_deckung` and
 * `lead_lifecycle` publish none — one describes what is covered, the other which statuses a lead
 * walks through; neither is the shape of a payload — and `insurance_core` DERIVES its policy-data
 * schema at publish from the document and its files (SDD-010, C-25), so nobody edits that one
 * either.
 */
export const editsAPublishedSchema = (type: AssetType): boolean => type === 'group_core';

/** The `insurance_core` schema is derived at publish; the editor shows it, never edits it. */
export const derivesItsSchema = (type: AssetType): boolean => type === 'insurance_core';

/**
 * The name a "next version" would take: `insurance_core.v1` → `insurance_core.v2`.
 *
 * A SUGGESTION, always editable. The suffix is part of the name and the service does not derive
 * it, so a name that does not end in `.v<n>` yields no suggestion rather than a guess — appending
 * `.v2` to `insurance_core.experimental` would mint an identity nobody chose.
 */
export function nextVersionName(name: string): string {
  const match = /^(.*\.v)(\d+)$/.exec(name);
  if (match?.[1] === undefined || match[2] === undefined) return '';
  return `${match[1]}${String(Number(match[2]) + 1)}`;
}

/** `insurance_core.v2` is a DRAFT until it is published; only a draft may be edited. */
export const isPublished = (status: string | undefined): boolean =>
  status === 'ASSET_STATUS_PUBLISHED';

/** A discarded draft is as immutable as a published one (migrations/000005). */
export const isDiscarded = (status: string | undefined): boolean =>
  status === 'ASSET_STATUS_DISCARDED';

/** Only a DRAFT takes a write; PUBLISHED and DISCARDED answer 409 `ASSET_NOT_EDITABLE`. */
export const isEditable = (status: string | undefined): boolean =>
  !isPublished(status) && !isDiscarded(status);

/** `DRAFT` / `PUBLISHED` / `DISCARDED` — the chip's status word, without the enum prefix. */
export const statusWordOf = (status: string | undefined): 'DRAFT' | 'PUBLISHED' | 'DISCARDED' =>
  isPublished(status) ? 'PUBLISHED' : isDiscarded(status) ? 'DISCARDED' : 'DRAFT';
